import {useAppDispatch, useAppSelector} from "../redux/stateHooks";
import {useMinimumQuantity} from "./useMinimumQuantity";
import {setDisplayQuantity, setPendingQuantity, setQuantity} from "../redux/slices/addToCart";
import {useEffect, useMemo} from "react";
import {debounce} from "lodash";

export const useQuantity = () => {
    const dispatch = useAppDispatch();
    const {minimumSaleQuantity} = useMinimumQuantity();
    const quantity = useAppSelector((state) => state.addToCart.value.quantity);
    const displayQuantity = useAppSelector((state) => state.addToCart.value.displayQuantity);

    const update: (quantity: string | number) => void = (quantity) => {
        // Convert quantity to a number
        quantity = Number(quantity);

        // If the value is not a number, return
        if (isNaN(quantity)) return;

        // Set the pending state to true
        dispatch(setPendingQuantity(true));

        // Update the global state with the new value, intentionally not normalized to the min and or max values
        dispatch(setDisplayQuantity(quantity));

        // Update the global state with the new value or undefined if it is invalid (debounced)
        debounceStateChange(quantity);
    };

    // When the user changes the value, update the quantity in the redux store and debounce the change
    const debounceStateChange = useMemo(() => debounce((quantity: number) => {
        // If quantity is less than the minimum quantity, set it to the minimum quantity instead of the quantity passed in as an argument
        dispatch(setQuantity(quantity));

        // Set the pending state to false
        dispatch(setPendingQuantity(false));
    }, 350), [minimumSaleQuantity]);

    // When the component unmounts, cancel the debounced function
    useEffect(() => () => debounceStateChange.cancel(), []);

    return {
        update,
        displayQuantity,
        quantity
    };
}